import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SumsubWebSdk from '@sumsub/websdk-react';
import { useQuery } from '@tanstack/react-query';
import { Drawer, message } from 'antd';
import { useHistory } from 'hooks/generic/useHistory';
import { useToken } from 'hooks/user/useToken';
import { useAppDispatch } from 'appRedux/hooks';
import { getAccountCreatorData, getUserData } from 'appRedux/restApi';
import { createApplicant, getSumsubAccessToken } from 'appRedux/restApi/SumSub';
import { setSession } from 'services/ApiClient';
import { getDocumentLanguage } from 'utils/bom-dom';
import { EAccountRouteKeys } from 'constants/account/account-route-keys';
import {
  ESumsubApplicantStatus,
  ESumsubKeys
} from 'constants/account/sumsub-keys';
import { RestAxiosError } from 'interfaces';

type KYCDrawerProps = {
  closePage: () => void;
  currentApplicantStatus?: string;
};

export const KYCDrawer: FC<KYCDrawerProps> = ({
  closePage,
  currentApplicantStatus
}) => {
  const [callCreateApplicant, setCallCreateApplicant] = useState(false);
  const [callGetSumsubToken, setCallGetSumsubToken] = useState(false);
  const [sumsubToken, setSumsubToken] = useState('');
  const { getUserToken } = useToken();
  const { t } = useTranslation();
  const { push } = useHistory();
  const dispatch = useAppDispatch();
  const userToken = getUserToken();

  const {
    data,
    error,
    refetch: refetchApplicant
  } = useQuery<Awaited<ReturnType<typeof createApplicant>>, RestAxiosError>({
    queryKey: [ESumsubKeys.CREATE_APPLICANT, userToken],
    queryFn: () => createApplicant(userToken as string),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: callCreateApplicant
  });

  const {
    data: sumsubData,
    error: sumsubError,
    refetch
  } = useQuery<
    Awaited<ReturnType<typeof getSumsubAccessToken>>,
    RestAxiosError
  >({
    queryKey: [ESumsubKeys.SUMSUB_ACCESS_TOKEN, userToken],
    queryFn: () => getSumsubAccessToken(userToken as string),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: callGetSumsubToken
  });

  const onCompleted = () => {
    setSession(userToken);
    dispatch(getAccountCreatorData());
    dispatch(getUserData());
    push(EAccountRouteKeys.ACCOUNT_PROFILE_INFO);
  };

  const messageHandler = (data: any, payload: any) => {
    if (
      data === 'idCheck.onApplicantStatusChanged' &&
      payload?.reviewStatus === ESumsubApplicantStatus.COMPLETED &&
      payload?.reviewResult?.reviewAnswer === 'GREEN'
    ) {
      message.success(t('account.accountVerified'));
      onCompleted();
    }
  };

  const errorHandler = (error: any) => {
    message.error(error);
    closePage();
  };

  const accessTokenExpirationHandler = () => {
    refetch();
    return Promise.resolve('');
  };

  useEffect(() => {
    if (currentApplicantStatus) {
      setCallGetSumsubToken(true);
      refetch();
    } else {
      setCallCreateApplicant(true);
      refetchApplicant();
    }
  }, [currentApplicantStatus]);

  useEffect(() => {
    if (data) {
      setCallGetSumsubToken(true);
      refetch();
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      message.error(error.response?.data?.message);
      closePage();
    }
  }, [error]);

  useEffect(() => {
    if (sumsubError) {
      message.error(sumsubError.response?.data?.message);
      closePage();
    }
  }, [sumsubError]);

  useEffect(() => {
    if (sumsubData) {
      setSumsubToken(sumsubData.data.token);
    }
  }, [sumsubData]);

  return sumsubToken ? (
    <Drawer open width="100%" closable={false}>
      <SumsubWebSdk
        accessToken={sumsubToken}
        expirationHandler={accessTokenExpirationHandler}
        config={{ lang: getDocumentLanguage('short') }}
        onMessage={messageHandler}
        onError={errorHandler}
      />
    </Drawer>
  ) : null;
};
